import { AnimatePresence, useIsPresent } from "framer-motion";
import { useCallback, useRef } from "react";
import { useLocation, useOutlet } from "react-router";

import { consumeSkipNextTransition } from "./dragOffset";
import { PageTransition } from "./PageTransition";
import {
  consumeTransitionDirection,
  type TransitionDirection,
} from "./transitionDirection";

/**
 * Renders the current outlet, freezing it only during exit animations.
 * While present: always shows the latest outlet (so inner route changes propagate).
 * During exit (useIsPresent = false): holds the last content for the fade-out.
 */
function FrozenOutlet() {
  const outlet = useOutlet();
  const isPresent = useIsPresent();
  const frozenRef = useRef(outlet);

  if (isPresent) {
    frozenRef.current = outlet;
  }

  return frozenRef.current;
}

interface AnimatedOutletProps {
  /** Override the default location.pathname key */
  transitionKey?: string;
  /** Animate the first mount; never over already-visible content */
  animateInitial?: boolean;
}

export function AnimatedOutlet({
  transitionKey,
  animateInitial = true,
}: AnimatedOutletProps) {
  const location = useLocation();
  const key = transitionKey ?? location.pathname;

  // Capture direction and mode when the transition key changes.
  // Both persist for the full animation cycle so that clearing direction
  // in handleEnterComplete doesn't flip the AnimatePresence mode mid-lifecycle.
  const directionRef = useRef<TransitionDirection>(null);
  const skipAnimationRef = useRef(false);
  const modeRef = useRef<"wait" | "sync">("wait");
  const prevKeyRef = useRef(key);

  if (prevKeyRef.current !== key) {
    const dir = consumeTransitionDirection();
    const skip = consumeSkipNextTransition();
    directionRef.current = dir;
    skipAnimationRef.current = skip;
    modeRef.current = dir ? "sync" : "wait";
    prevKeyRef.current = key;
  }

  const direction = directionRef.current;
  const skipAnimation = skipAnimationRef.current;

  const handleEnterComplete = useCallback(() => {
    directionRef.current = null;
    const scrollY = (location.state as { scrollY?: number } | null)?.scrollY;
    if (scrollY != null) {
      window.scrollTo(0, scrollY);
    }
  }, [location.state]);

  return (
    <div className="relative grid grid-cols-[minmax(0,1fr)] overflow-x-clip [&>*]:col-start-1 [&>*]:row-start-1">
      <AnimatePresence
        mode={modeRef.current}
        initial={animateInitial}
        custom={{ direction, skipAnimation }}
      >
        <PageTransition
          key={key}
          onEnterComplete={handleEnterComplete}
          direction={direction}
          skipAnimation={skipAnimation}
        >
          <FrozenOutlet />
        </PageTransition>
      </AnimatePresence>
    </div>
  );
}
